// /chatbot — the overview screen: master switch, headline counts, current config.

import { useEffect, useState } from "react";

import { http } from "../api";
import { useSession } from "../App";
import { Badge, Confirm, Num, SkeletonRows, Spinner, useAsync, useToast } from "../ui";
import { StatTile } from "../charts";
import { ErrorBanner, errMessage } from "./shared";

interface ChatbotOverviewSettings {
  enabled: boolean;
  greeting_message: string;
  fallback_message: string;
  session_timeout_minutes: number;
  ai_enabled: boolean;
  ai_provider: string;
}

interface ChatbotStats {
  total_sessions: number;
  active_sessions: number;
  messages_handled: number;
  agent_transfers: number;
}

interface ChatbotSettingsEnvelope {
  settings?: Partial<ChatbotOverviewSettings>;
  stats?: Partial<ChatbotStats>;
}

const ZERO_STATS: ChatbotStats = {
  total_sessions: 0,
  active_sessions: 0,
  messages_handled: 0,
  agent_transfers: 0,
};

export function ChatbotView() {
  const { can } = useSession();
  const toast = useToast();
  const canWrite = can("settings.chatbot", "write");

  const { data, error, loading, reload } = useAsync<ChatbotSettingsEnvelope>(
    () => http.get<ChatbotSettingsEnvelope>("/chatbot/settings"),
    []
  );

  const [enabled, setEnabled] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);

  useEffect(() => {
    if (data?.settings) setEnabled(Boolean(data.settings.enabled));
  }, [data]);

  const settings = data?.settings;
  const stats = { ...ZERO_STATS, ...(data?.stats ?? {}) };

  const setChatbotEnabled = async (next: boolean) => {
    setToggling(true);
    try {
      await http.put("/chatbot/settings", { enabled: next });
      setEnabled(next);
      toast.push(next ? "Chatbot enabled" : "Chatbot disabled", "good");
    } catch (err) {
      toast.push(errMessage(err, "Failed to toggle the chatbot"), "critical");
    } finally {
      setToggling(false);
      setConfirmDisable(false);
    }
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Chatbot</h1>
          <div className="sub">Automated first response, flows and handover.</div>
        </div>
        <div className="page-actions">
          <span className={`badge ${enabled ? "good" : "neutral"}`}>
            <span className="dot" />
            {enabled ? "Active" : "Inactive"}
          </span>
          <button
            className={`btn${enabled ? " danger" : ""}`}
            disabled={!canWrite || toggling || loading}
            title={canWrite ? undefined : "Needs settings.chatbot write"}
            onClick={() => (enabled ? setConfirmDisable(true) : void setChatbotEnabled(true))}
          >
            {toggling ? <Spinner /> : "⏻"} {enabled ? "Disable" : "Enable"}
          </button>
        </div>
      </div>

      <ErrorBanner error={error} what="Could not load the chatbot configuration" />

      {loading ? (
        <div className="card">
          <SkeletonRows n={4} cols={4} />
        </div>
      ) : (
        <>
          <div className="grid c4" style={{ marginBottom: "var(--sp-4)" }}>
            <StatTile label="Total sessions" value={stats.total_sessions} />
            <StatTile label="Active sessions" value={stats.active_sessions} />
            <StatTile label="Messages handled" value={stats.messages_handled} />
            <StatTile label="Escalations" value={stats.agent_transfers} />
          </div>

          <div className="card flush">
            <div className="card-head">
              <h3>Current configuration</h3>
              <a className="btn sm" href="/chatbot/settings">
                ⚙ Edit settings
              </a>
            </div>
            <div className="card-body grid c2">
              <div>
                <h4 className="muted">Greeting message</h4>
                <p style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>
                  {settings?.greeting_message || <span className="muted">Not configured</span>}
                </p>
              </div>
              <div>
                <h4 className="muted">Fallback message</h4>
                <p style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>
                  {settings?.fallback_message || <span className="muted">Not configured</span>}
                </p>
              </div>
              <div>
                <h4 className="muted">Session timeout</h4>
                <p style={{ marginTop: 6 }}>
                  <Num value={settings?.session_timeout_minutes ?? 0} /> minutes
                </p>
              </div>
              <div>
                <h4 className="muted">AI provider</h4>
                <p style={{ marginTop: 6 }}>
                  {settings?.ai_enabled ? (
                    <Badge tone="good">{settings.ai_provider || "Not configured"}</Badge>
                  ) : (
                    <Badge tone="neutral">Disabled</Badge>
                  )}
                </p>
              </div>
            </div>
            <div className="card-foot">
              <button className="btn sm ghost" onClick={reload}>
                ⟲ Refresh
              </button>
            </div>
          </div>
        </>
      )}

      <Confirm
        open={confirmDisable}
        onClose={() => setConfirmDisable(false)}
        onConfirm={() => void setChatbotEnabled(false)}
        title="Disable the chatbot?"
        body="Incoming WhatsApp messages will stop getting an automated reply until it is enabled again."
        danger
      />
    </div>
  );
}
